import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { addChat, getTelegram, recentChats, removeChat, saveTelegram, sendTest, telegramPeople, updateChat } from "@/lib/integrationsApi";

const reasonOf = err => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).join("; ");
  return err?.message || "unknown error";
};

const when = iso => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/**
 * Admin → Integrations → Telegram (owner, 2026-10-25): the day-end summary to the
 * owner's phone after each day change. The restaurant's own server sends it over
 * the restaurant's own internet (final-product.md §13.5).
 */
export default function TelegramPanel({ part }) {
  const { token } = usePOS();
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [newToken, setNewToken] = useState("");
  // Chat id -> connected person, so a chat that is also a person under People is
  // named as the duplicate it is (old step-1 chats, 2026-11-01).
  const [people, setPeople] = useState({});
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await getTelegram(token));
      setLoadError(null);
      if (part === "groups") {
        const rows = await telegramPeople(token).catch(() => []);
        setPeople(Object.fromEntries((rows || []).map(p => [p.chat_id, p.name])));
      }
    } catch (err) {
      setLoadError(reasonOf(err));
    }
  }, [token, part]);

  useEffect(() => { load(); }, [load]);

  const run = async (work, success) => {
    setBusy(true);
    try {
      await work();
      if (success) toast.success(success);
      await load();
    } catch (err) {
      toast.error(`Not saved — ${reasonOf(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <p className="text-meta text-destructive" role="alert">Telegram settings not loaded — {loadError}</p>;
  if (!state) return <p className="text-meta text-muted-foreground">Loading…</p>;

  const saveToken = () => run(async () => {
    await saveTelegram({ bot_token: newToken, day_end_enabled: state.day_end_enabled }, token);
    setNewToken("");
  }, "Bot token saved");

  const toggleDayEnd = on => run(
    () => saveTelegram({ day_end_enabled: on }, token),
    on ? "Day-end summary switched on" : "Day-end summary switched off",
  );

  const add = (id, name) => run(() => addChat({ chat_id: id, label: name || null }, token), "Group added");

  const test = async id => {
    setBusy(true);
    try {
      const res = await sendTest(id, token);
      if (res.ok) toast.success("Test message sent — check Telegram");
      else toast.error(`Test not sent — ${res.reason}`);
    } catch (err) {
      toast.error(`Test not sent — ${reasonOf(err)}`);
    } finally {
      setBusy(false);
      load();
    }
  };

  const find = async () => {
    setBusy(true);
    try {
      const res = await recentChats(token);
      setFound(res);
    } catch (err) {
      setFound({ chats: [], reason: reasonOf(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="telegram-panel">
      {part !== "groups" && <>
      <div className="rounded-md border bg-card p-3 space-y-2">
        <h3 className="text-body font-bold">Telegram</h3>
        <p className="text-meta text-muted-foreground">
          Paste the token from @BotFather to connect the bot. After each day change the day's summary goes to the people
          whose Day-end summary is on (People, Roles &amp; defaults) and to the chats under Groups. It goes over the
          restaurant's internet; if the internet is down it waits and sends later.
        </p>
        <div className="text-meta" data-testid="telegram-status">
          {state.last_sent_at && <div>Last sent {when(state.last_sent_at)}</div>}
          {state.last_error && <div className="text-destructive">Last failure: {state.last_error}</div>}
          {state.queued > 0 && <div>{state.queued} waiting to send</div>}
          {state.bot_poll_error && <div className="text-destructive">Bot not reading messages: {state.bot_poll_error}</div>}
          {!state.last_sent_at && !state.last_error && state.queued === 0 && (
            <div className="text-muted-foreground">Nothing sent yet.</div>
          )}
        </div>
      </div>

      <div className="rounded-md border bg-card p-3 space-y-2">
        <label className="text-body font-medium" htmlFor="telegram-token">Bot token</label>
        <p className="text-meta text-muted-foreground">
          Create a bot with @BotFather in Telegram and paste the token it gives you. {state.token_set ? `Set · ${state.token_hint}` : "Not set"}
        </p>
        <div className="flex gap-2">
          <Input id="telegram-token" data-testid="telegram-token" type="password" autoComplete="off"
                 value={newToken} onChange={e => setNewToken(e.target.value)}
                 placeholder={state.token_set ? "Paste a new token to replace it" : "123456789:AA…"} />
          <Button variant="action" disabled={busy || !newToken.trim()} onClick={saveToken}>Save</Button>
          {state.token_set && (
            <Button variant="ghost" className="text-danger" disabled={busy} data-testid="telegram-remove-token"
                    onClick={() => run(() => saveTelegram({ remove_token: true, day_end_enabled: false }, token), "Bot token removed")}>
              Remove
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between pt-2">
          <div>
            <div className="text-body font-medium">Day-end summary</div>
            <div className="text-meta text-muted-foreground">
              {state.day_end_enabled ? `On since ${state.enabled_from}. Earlier days are not sent.` : "Off"}
            </div>
          </div>
          <Switch data-testid="telegram-day-end" checked={state.day_end_enabled} disabled={busy || !state.token_set}
                  onCheckedChange={toggleDayEnd} />
        </div>
      </div>

      </>}
      {part !== "bot" && (
      <div className="rounded-md border bg-card p-3 space-y-2">
        <div className="text-body font-medium">Groups</div>
        <p className="text-meta text-muted-foreground">
          Group chats that get the day-end summary (a partners' group, the accounts group). Add the bot to the group,
          send /start there, then Find groups. Staff are connected one by one under People, not here.
        </p>
        {state.chats.length === 0 && <p className="text-meta text-muted-foreground">No groups yet.</p>}
        {state.chats.map(c => (
          <div key={c.id} className="flex flex-wrap items-center gap-2 border-t pt-2 first:border-t-0 first:pt-0" data-testid={`telegram-chat-${c.id}`}>
            <span className="text-body flex-1 min-w-0 truncate">
              {c.label || "Chat"} <span className="text-muted-foreground tabular-nums">{c.chat_id}</span>
              {people[c.chat_id] && (
                <span className="block text-meta text-warning" data-testid={`telegram-chat-duplicate-${c.id}`}>
                  Also connected as {people[c.chat_id]} under People — remove it here and manage them there.
                </span>
              )}
            </span>
            <span className="text-meta text-muted-foreground">Day-end</span>
            <Switch checked={c.day_end_summary} disabled={busy}
                    onCheckedChange={on => run(() => updateChat(c.id, { day_end_summary: on }, token))} />
            <Button variant="outline" size="sm" disabled={busy || !state.token_set} data-testid={`telegram-test-${c.id}`}
                    onClick={() => test(c.chat_id)}>Send test message</Button>
            <Button variant="ghost" size="sm" disabled={busy}
                    onClick={() => run(() => removeChat(c.id, token), "Chat removed")}>Remove</Button>
          </div>
        ))}
        <div className="pt-2 border-t">
          <Button variant="outline" disabled={busy || !state.token_set} onClick={find} data-testid="telegram-find-groups">Find groups</Button>
        </div>
        {found && (
          <div className="text-meta pt-1" data-testid="telegram-found">
            {found.reason && <div className="text-destructive">Not found — {found.reason}</div>}
            {!found.reason && !found.chats.some(c => String(c.chat_id).startsWith("-")) && <div className="text-muted-foreground">No group has messaged the bot yet. Add the bot to the group, send /start there, and try again.</div>}
            {/* Groups only (negative chat ids): people connect under People. */}
            {found.chats.filter(c => String(c.chat_id).startsWith("-")).map(c => (
              <div key={c.chat_id} className="flex items-center gap-2">
                <span>{c.name} <span className="text-muted-foreground tabular-nums">{c.chat_id}</span></span>
                <Button size="sm" variant="outline" disabled={busy || state.chats.some(x => x.chat_id === c.chat_id)}
                        onClick={() => add(c.chat_id, c.name)}>Add</Button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
